import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import LessonLibrary from '@/features/connectlessen/library/LessonLibrary';
import { printLesson } from '@/features/connectlessen/lessonPrint';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  AlertCircle,
  ArrowLeft,
  BookOpen,
  Calendar,
  CheckCircle2,
  Heart,
  Mail,
  Megaphone,
  MoreVertical,
  Pencil,
  Plus,
  Printer,
  Search,
  Trash2,
  Users2,
  UserCheck,
  UserPlus,
  XCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCIES = [
  { value: 'wekelijks', label: 'Wekelijks' },
  { value: 'tweewekelijks', label: 'Tweewekelijks' },
  { value: 'maandelijks', label: 'Maandelijks' },
  { value: 'anders', label: 'Anders' },
];

const EMPTY_GROUP_FORM = {
  name: '',
  description: '',
  leader_user_id: '',
  co_leader_user_ids: [],
  frequency: 'tweewekelijks',
  frequency_note: '',
  location: '',
  max_participants: '',
  is_active: true,
  color: '#0d9488',
};

const EMPTY_MEETING_FORM = {
  meeting_date: '',
  start_time: '',
  end_time: '',
  theme: '',
  lesson_id: '',
  location: '',
  notes: '',
};

const getLabel = (options, value) => options.find((option) => option.value === value)?.label || value;

const ConnectgroepenPage = () => {
  const { user, hasCapability } = useAuth();
  const canManageGroups = hasCapability('connectgroepen_manage');

  const [activeTab, setActiveTab] = useState('groepen');
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [members, setMembers] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [resources, setResources] = useState([]);
  const [lessonWriters, setLessonWriters] = useState([]);

  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [detailTab, setDetailTab] = useState('deelnemers');
  const [participants, setParticipants] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [attendanceStats, setAttendanceStats] = useState([]);
  const [branding, setBranding] = useState({ name: 'Zoea', logo_url: '' });

  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [attendanceMeeting, setAttendanceMeeting] = useState(null);
  const [attendanceDraft, setAttendanceDraft] = useState({});
  const [savingAttendance, setSavingAttendance] = useState(false);

  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState(EMPTY_GROUP_FORM);

  const [participantDialogOpen, setParticipantDialogOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);

  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [meetingForm, setMeetingForm] = useState(EMPTY_MEETING_FORM);

  const [roomBookingMeetingId, setRoomBookingMeetingId] = useState(null);
  const [roomBookingResourceId, setRoomBookingResourceId] = useState('');
  const [roomAvailability, setRoomAvailability] = useState([]);
  const [loadingRoomAvailability, setLoadingRoomAvailability] = useState(false);

  const [noteDraft, setNoteDraft] = useState({ prayer: '', announcement: '' });

  const [writerDialogOpen, setWriterDialogOpen] = useState(false);
  const [writerSource, setWriterSource] = useState('teamlid');
  const [writerUserId, setWriterUserId] = useState('');
  const [writerMemberSearch, setWriterMemberSearch] = useState('');
  const [writerMemberResults, setWriterMemberResults] = useState([]);
  const [writerInviteResult, setWriterInviteResult] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [groupsRes, teamRes, membersRes, lessonsRes, resourcesRes] = await Promise.all([
        axios.get(`${API}/connectgroepen`),
        axios.get(`${API}/team`).catch(() => ({ data: [] })),
        axios.get(`${API}/members`).catch(() => ({ data: [] })),
        axios.get(`${API}/connectlessen`).catch(() => ({ data: [] })),
        axios.get(`${API}/resources`).catch(() => ({ data: [] })),
      ]);
      setGroups(groupsRes.data);
      setTeamMembers(teamRes.data);
      setMembers(membersRes.data);
      setLessons(lessonsRes.data);
      setResources(resourcesRes.data.filter((r) => r.type === 'room'));
      if (canManageGroups) {
        const writersRes = await axios.get(`${API}/connectgroepen/lesson-writers`).catch(() => ({ data: [] }));
        setLessonWriters(writersRes.data);
      }
    } catch (error) {
      toast.error('Kon connectgroepen niet laden');
    } finally {
      setLoading(false);
    }
  }, [canManageGroups]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    axios.get(`${API}/organization/branding`)
      .then((res) => setBranding({ name: res.data?.name || 'Zoea', logo_url: res.data?.logo_url || '' }))
      .catch(() => setBranding({ name: 'Zoea', logo_url: '' }));
  }, []);

  const memberNameById = useMemo(() => {
    const map = {};
    teamMembers.forEach((m) => { map[m.id] = m.name; });
    return map;
  }, [teamMembers]);

  const congregationMemberNameById = useMemo(() => {
    const map = {};
    members.forEach((m) => { map[m.id] = m.name; });
    return map;
  }, [members]);

  const connectLeaders = useMemo(
    () => teamMembers.filter((m) => (m.roles || []).includes('connectleider')),
    [teamMembers]
  );

  const lessonById = useMemo(() => {
    const map = {};
    lessons.forEach((l) => { map[l.id] = l; });
    return map;
  }, [lessons]);

  const publishedLessons = useMemo(() => lessons.filter((l) => l.status === 'gepubliceerd'), [lessons]);

  const selectedGroup = groups.find((g) => g.id === selectedGroupId) || null;
  const canManageSelectedGroup = selectedGroup
    ? canManageGroups
      || user?.id === selectedGroup.leader_user_id
      || (selectedGroup.co_leader_user_ids || []).includes(user?.id)
    : false;
  const prayerPoints = selectedGroup?.prayer_points || [];
  const announcements = selectedGroup?.announcements || [];
  const lessonBiblePassages = currentLesson?.lesson?.bible_passages || [];
  const lessonQuestions = currentLesson?.lesson?.questions || [];

  const openGroupDetail = async (groupId) => {
    setSelectedGroupId(groupId);
    setDetailTab('deelnemers');
    try {
      const [participantsRes, meetingsRes, lessonRes, statsRes] = await Promise.all([
        axios.get(`${API}/connectgroepen/${groupId}/participants`),
        axios.get(`${API}/connectgroepen/${groupId}/meetings`),
        axios.get(`${API}/connectgroepen/${groupId}/current-lesson`),
        axios.get(`${API}/connectgroepen/${groupId}/attendance-stats`),
      ]);
      setParticipants(participantsRes.data);
      setMeetings(meetingsRes.data);
      setCurrentLesson(lessonRes.data);
      setAttendanceStats(statsRes.data);
    } catch (error) {
      toast.error('Kon groepdetails niet laden');
    }
  };

  const refreshGroupDetail = () => {
    if (selectedGroupId) openGroupDetail(selectedGroupId);
  };

  const resetGroupForm = () => {
    setEditingGroup(null);
    setGroupForm(EMPTY_GROUP_FORM);
  };

  const openEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name,
      description: group.description || '',
      leader_user_id: group.leader_user_id || '',
      co_leader_user_ids: group.co_leader_user_ids || [],
      frequency: group.frequency || 'tweewekelijks',
      frequency_note: group.frequency_note || '',
      location: group.location || '',
      max_participants: group.max_participants || '',
      is_active: group.is_active,
      color: group.color || '#0d9488',
    });
    setGroupDialogOpen(true);
  };

  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    if (!groupForm.name) {
      toast.error('Naam is verplicht');
      return;
    }
    const payload = {
      ...groupForm,
      leader_user_id: groupForm.leader_user_id || null,
      max_participants: groupForm.max_participants ? Number(groupForm.max_participants) : null,
    };
    try {
      if (editingGroup) {
        await axios.put(`${API}/connectgroepen/${editingGroup.id}`, payload);
        toast.success('Connectgroep bijgewerkt');
      } else {
        await axios.post(`${API}/connectgroepen`, payload);
        toast.success('Connectgroep aangemaakt');
      }
      setGroupDialogOpen(false);
      resetGroupForm();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Weet je zeker dat je deze connectgroep wilt verwijderen?')) return;
    try {
      await axios.delete(`${API}/connectgroepen/${groupId}`);
      toast.success('Connectgroep verwijderd');
      if (selectedGroupId === groupId) setSelectedGroupId(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  // ---- Deelnemers ----
  const searchMembers = async (query) => {
    setMemberSearch(query);
    if (!query) { setMemberResults([]); return; }
    try {
      const response = await axios.get(`${API}/members`, { params: { search: query } });
      setMemberResults(response.data);
    } catch (error) {
      toast.error('Zoeken mislukt');
    }
  };

  const addParticipant = async (memberId) => {
    try {
      await axios.post(`${API}/connectgroepen/${selectedGroupId}/participants`, { member_id: memberId });
      toast.success('Deelnemer toegevoegd');
      setParticipantDialogOpen(false);
      setMemberSearch('');
      setMemberResults([]);
      refreshGroupDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Toevoegen mislukt');
    }
  };

  const removeParticipant = async (membershipId) => {
    if (!window.confirm('Deelnemer verwijderen uit deze connectgroep?')) return;
    try {
      await axios.delete(`${API}/connectgroepen/${selectedGroupId}/participants/${membershipId}`);
      toast.success('Deelnemer verwijderd');
      refreshGroupDetail();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  // ---- Agenda / bijeenkomsten ----
  const resetMeetingForm = () => {
    setEditingMeeting(null);
    setMeetingForm(EMPTY_MEETING_FORM);
  };

  const openEditMeeting = (meeting) => {
    setEditingMeeting(meeting);
    setMeetingForm({
      meeting_date: meeting.meeting_date,
      start_time: meeting.start_time,
      end_time: meeting.end_time,
      theme: meeting.theme || '',
      lesson_id: meeting.lesson_id || '',
      location: meeting.location || '',
      notes: meeting.notes || '',
    });
    setMeetingDialogOpen(true);
  };

  const handleMeetingSubmit = async (e) => {
    e.preventDefault();
    if (!meetingForm.meeting_date || !meetingForm.start_time || !meetingForm.end_time) {
      toast.error('Datum, starttijd en eindtijd zijn verplicht');
      return;
    }
    const payload = { ...meetingForm, lesson_id: meetingForm.lesson_id || null };
    try {
      if (editingMeeting) {
        await axios.put(`${API}/connectgroepen/${selectedGroupId}/meetings/${editingMeeting.id}`, payload);
        toast.success('Bijeenkomst bijgewerkt');
      } else {
        await axios.post(`${API}/connectgroepen/${selectedGroupId}/meetings`, payload);
        toast.success('Bijeenkomst toegevoegd');
      }
      setMeetingDialogOpen(false);
      resetMeetingForm();
      refreshGroupDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Bijeenkomst verwijderen?')) return;
    try {
      await axios.delete(`${API}/connectgroepen/${selectedGroupId}/meetings/${meetingId}`);
      toast.success('Bijeenkomst verwijderd');
      refreshGroupDetail();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const openRoomBookingDialog = async (meetingId) => {
    setRoomBookingMeetingId(meetingId);
    setRoomBookingResourceId('');
    setLoadingRoomAvailability(true);
    try {
      const response = await axios.get(`${API}/connectgroepen/${selectedGroupId}/meetings/${meetingId}/room-availability`);
      setRoomAvailability(response.data);
    } catch (error) {
      toast.error('Kon beschikbaarheid niet laden');
      setRoomAvailability([]);
    } finally {
      setLoadingRoomAvailability(false);
    }
  };

  const handleBookRoom = async () => {
    if (!roomBookingResourceId) {
      toast.error('Kies eerst een ruimte');
      return;
    }
    try {
      const response = await axios.post(`${API}/connectgroepen/${selectedGroupId}/meetings/${roomBookingMeetingId}/room-booking`, {
        resource_id: roomBookingResourceId,
      });
      if (response.data.booking_status === 'pending') {
        toast.success('Reserveringsverzoek verstuurd, wacht op goedkeuring');
      } else {
        toast.success('Ruimte gereserveerd');
      }
      setRoomBookingMeetingId(null);
      setRoomBookingResourceId('');
      refreshGroupDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reserveren mislukt');
    }
  };

  const handleCancelRoomBooking = async (meetingId) => {
    if (!window.confirm('Ruimtereservering annuleren?')) return;
    try {
      await axios.delete(`${API}/connectgroepen/${selectedGroupId}/meetings/${meetingId}/room-booking`);
      toast.success('Reservering geannuleerd');
      refreshGroupDetail();
    } catch (error) {
      toast.error('Annuleren mislukt');
    }
  };

  // ---- Aanwezigheid ----
  const openAttendanceDialog = (meeting) => {
    const existingByMember = {};
    (meeting.attendance || []).forEach((entry) => { existingByMember[entry.member_id] = entry.present; });
    const draft = {};
    participants.forEach((p) => {
      draft[p.member_id] = Object.prototype.hasOwnProperty.call(existingByMember, p.member_id)
        ? existingByMember[p.member_id]
        : true;
    });
    setAttendanceMeeting(meeting);
    setAttendanceDraft(draft);
    setAttendanceDialogOpen(true);
  };

  const toggleAttendance = (memberId) => {
    setAttendanceDraft((current) => ({ ...current, [memberId]: !current[memberId] }));
  };

  const saveAttendance = async () => {
    if (!attendanceMeeting) return;
    setSavingAttendance(true);
    try {
      const entries = Object.entries(attendanceDraft).map(([member_id, present]) => ({ member_id, present }));
      const response = await axios.put(
        `${API}/connectgroepen/${selectedGroupId}/meetings/${attendanceMeeting.id}/attendance`,
        { entries }
      );
      setAttendanceMeeting(response.data);
      toast.success('Aanwezigheid opgeslagen');
      refreshGroupDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSavingAttendance(false);
    }
  };

  const sendAttendanceEmail = async () => {
    if (!attendanceMeeting) return;
    try {
      const response = await axios.post(
        `${API}/connectgroepen/${selectedGroupId}/meetings/${attendanceMeeting.id}/attendance/email`
      );
      toast.success(response.data?.message || 'Overzicht verstuurd');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Versturen mislukt');
    }
  };

  // ---- Gebedspunten / mededelingen ----
  const addNote = async (kind) => {
    const text = kind === 'prayer' ? noteDraft.prayer : noteDraft.announcement;
    if (!text) return;
    const path = kind === 'prayer' ? 'prayer-points' : 'announcements';
    try {
      await axios.post(`${API}/connectgroepen/${selectedGroupId}/${path}`, { text });
      setNoteDraft((current) => ({ ...current, [kind === 'prayer' ? 'prayer' : 'announcement']: '' }));
      refreshGroupDetail();
    } catch (error) {
      toast.error('Toevoegen mislukt');
    }
  };

  const removeNote = async (kind, itemId) => {
    const path = kind === 'prayer' ? 'prayer-points' : 'announcements';
    try {
      await axios.delete(`${API}/connectgroepen/${selectedGroupId}/${path}/${itemId}`);
      refreshGroupDetail();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  // ---- Connectlesmakers ----
  const searchWriterMembers = async (query) => {
    setWriterMemberSearch(query);
    if (!query) { setWriterMemberResults([]); return; }
    try {
      const response = await axios.get(`${API}/members`, { params: { search: query } });
      setWriterMemberResults(response.data);
    } catch (error) {
      toast.error('Zoeken mislukt');
    }
  };

  const availableTeamMembersForWriter = useMemo(
    () => teamMembers.filter((m) => !lessonWriters.some((w) => w.id === m.id)),
    [teamMembers, lessonWriters]
  );

  const addWriterFromExisting = async () => {
    if (!writerUserId) {
      toast.error('Kies eerst een teamlid');
      return;
    }
    try {
      await axios.post(`${API}/connectgroepen/lesson-writers`, { user_id: writerUserId });
      toast.success('Connectlesmaker toegevoegd');
      setWriterDialogOpen(false);
      setWriterUserId('');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Toevoegen mislukt');
    }
  };

  const addWriterFromMember = async (memberId) => {
    try {
      const response = await axios.post(`${API}/connectgroepen/lesson-writers/from-member`, { member_id: memberId });
      setWriterInviteResult(response.data);
      toast.success('Uitnodiging verstuurd');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Uitnodigen mislukt');
    }
  };

  const removeWriter = async (membershipId) => {
    if (!window.confirm('Connectlesmaker uit het rooster verwijderen?')) return;
    try {
      await axios.delete(`${API}/connectgroepen/lesson-writers/${membershipId}`);
      toast.success('Verwijderd uit rooster');
      fetchAll();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-96 bg-slate-200 rounded-2xl"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 print:hidden" data-testid="connectgroepen-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Connectgroepen</h1>
            <p className="text-slate-500">Kleine groepen, deelnemers, agenda en connectlessen</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="groepen">Groepen</TabsTrigger>
            <TabsTrigger value="lessen">Lessen</TabsTrigger>
            {canManageGroups && <TabsTrigger value="connectlesmakers">Connectlesmakers</TabsTrigger>}
          </TabsList>

          <TabsContent value="groepen" className="space-y-6 mt-4">
            {selectedGroup ? (
              <div className="space-y-4">
                <Button variant="ghost" size="sm" onClick={() => setSelectedGroupId(null)} className="gap-2">
                  <ArrowLeft className="w-4 h-4" /> Terug naar groepen
                </Button>

                <Card className="border-0 shadow-sm">
                  <CardContent className="p-5 flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-slate-900">{selectedGroup.name}</h2>
                      <p className="text-sm text-slate-500">
                        {getLabel(FREQUENCIES, selectedGroup.frequency)}
                        {selectedGroup.location ? ` · ${selectedGroup.location}` : ''}
                        {selectedGroup.leader_user_id ? ` · Leider: ${memberNameById[selectedGroup.leader_user_id] || '-'}` : ''}
                      </p>
                    </div>
                    {canManageSelectedGroup && (
                      <Button variant="outline" size="sm" onClick={() => openEditGroup(selectedGroup)}>
                        <Pencil className="w-4 h-4 mr-2" /> Bewerken
                      </Button>
                    )}
                  </CardContent>
                </Card>

                <Tabs value={detailTab} onValueChange={setDetailTab}>
                  <TabsList className="w-full max-w-full justify-start overflow-x-auto">
                    <TabsTrigger value="deelnemers">Deelnemers</TabsTrigger>
                    <TabsTrigger value="agenda">Agenda</TabsTrigger>
                    <TabsTrigger value="gebedspunten">Gebedspunten</TabsTrigger>
                    <TabsTrigger value="mededelingen">Mededelingen</TabsTrigger>
                    <TabsTrigger value="les">Les</TabsTrigger>
                    <TabsTrigger value="aanwezigheid">Aanwezigheid</TabsTrigger>
                  </TabsList>

                  <TabsContent value="deelnemers" className="mt-4 space-y-3">
                    {canManageSelectedGroup && (
                      <Button size="sm" onClick={() => setParticipantDialogOpen(true)} className="bg-teal-600 hover:bg-teal-700 text-white">
                        <UserPlus className="w-4 h-4 mr-2" /> Deelnemer toevoegen
                      </Button>
                    )}
                    {participants.length === 0 ? (
                      <p className="text-sm text-slate-500">Nog geen deelnemers.</p>
                    ) : (
                      <div className="space-y-2">
                        {participants.map((p) => (
                          <Card key={p.id} className="border-0 shadow-sm">
                            <CardContent className="p-4 flex items-center justify-between">
                              <div>
                                <p className="font-medium text-slate-900">{congregationMemberNameById[p.member_id] || p.member_id}</p>
                                <p className="text-xs text-slate-500">{p.role || 'Deelnemer'} · sinds {p.date_added}</p>
                              </div>
                              {canManageSelectedGroup && (
                                <Button variant="ghost" size="icon" onClick={() => removeParticipant(p.id)}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="agenda" className="mt-4 space-y-3">
                    {canManageSelectedGroup && (
                      <Button size="sm" onClick={() => { resetMeetingForm(); setMeetingDialogOpen(true); }} className="bg-teal-600 hover:bg-teal-700 text-white">
                        <Plus className="w-4 h-4 mr-2" /> Bijeenkomst plannen
                      </Button>
                    )}
                    {meetings.length === 0 ? (
                      <p className="text-sm text-slate-500">Nog geen bijeenkomsten gepland.</p>
                    ) : (
                      <div className="space-y-2">
                        {meetings.map((m) => (
                          <Card key={m.id} className="border-0 shadow-sm">
                            <CardContent className="p-4 flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                                  {m.meeting_date} · {m.start_time}-{m.end_time}
                                  {m.booking_status === 'pending' && (
                                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                                      Ruimte: wacht op goedkeuring
                                    </span>
                                  )}
                                  {m.booking_status === 'confirmed' && (
                                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                                      Ruimte gereserveerd
                                    </span>
                                  )}
                                  {(m.attendance || []).length > 0 && (
                                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 flex items-center gap-1">
                                      <UserCheck className="w-3 h-3" /> Aanwezigheid geregistreerd
                                    </span>
                                  )}
                                </p>
                                <p className="text-sm text-slate-500 truncate">
                                  {m.theme || (m.lesson_id ? lessonById[m.lesson_id]?.title : '') || 'Geen thema'}
                                  {m.location ? ` · ${m.location}` : ''}
                                </p>
                              </div>
                              {canManageSelectedGroup && (
                                <div className="flex items-center gap-2 shrink-0">
                                  {m.booking_id ? (
                                    <Button variant="outline" size="sm" onClick={() => handleCancelRoomBooking(m.id)}>Boeking annuleren</Button>
                                  ) : (
                                    <Button variant="outline" size="sm" onClick={() => openRoomBookingDialog(m.id)}>Ruimte boeken</Button>
                                  )}
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => openAttendanceDialog(m)}><UserCheck className="w-4 h-4 mr-2" />Aanwezigheid</DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => openEditMeeting(m)}><Pencil className="w-4 h-4 mr-2" />Bewerken</DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleDeleteMeeting(m.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Verwijderen</DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="gebedspunten" className="mt-4 space-y-3">
                    {canManageSelectedGroup && (
                      <div className="flex gap-2">
                        <Input
                          value={noteDraft.prayer}
                          onChange={(e) => setNoteDraft((c) => ({ ...c, prayer: e.target.value }))}
                          placeholder="Nieuw gebedspunt..."
                          className="rounded-xl"
                        />
                        <Button onClick={() => addNote('prayer')} className="bg-teal-600 hover:bg-teal-700 text-white shrink-0">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    {prayerPoints.length === 0 ? (
                      <p className="text-sm text-slate-500">Nog geen gebedspunten.</p>
                    ) : (
                      <div className="space-y-2">
                        {prayerPoints.map((item) => (
                          <div key={item.id} className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
                            <span className="text-sm text-slate-700 flex items-center gap-2"><Heart className="w-4 h-4 text-rose-400" />{item.text}</span>
                            {canManageSelectedGroup && (
                              <Button variant="ghost" size="icon" onClick={() => removeNote('prayer', item.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="mededelingen" className="mt-4 space-y-3">
                    {canManageSelectedGroup && (
                      <div className="flex gap-2">
                        <Input
                          value={noteDraft.announcement}
                          onChange={(e) => setNoteDraft((c) => ({ ...c, announcement: e.target.value }))}
                          placeholder="Nieuwe mededeling..."
                          className="rounded-xl"
                        />
                        <Button onClick={() => addNote('announcement')} className="bg-teal-600 hover:bg-teal-700 text-white shrink-0">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    {announcements.length === 0 ? (
                      <p className="text-sm text-slate-500">Nog geen mededelingen.</p>
                    ) : (
                      <div className="space-y-2">
                        {announcements.map((item) => (
                          <div key={item.id} className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
                            <span className="text-sm text-slate-700 flex items-center gap-2"><Megaphone className="w-4 h-4 text-amber-400" />{item.text}</span>
                            {canManageSelectedGroup && (
                              <Button variant="ghost" size="icon" onClick={() => removeNote('announcement', item.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="les" className="mt-4">
                    {currentLesson?.lesson ? (
                      <div className="space-y-3">
                        <div className="flex justify-end">
                          <Button variant="outline" size="sm" onClick={() => printLesson(currentLesson.lesson, selectedGroup.name, branding)}>
                            <Printer className="w-4 h-4 mr-2" /> Printen
                          </Button>
                        </div>
                        <Card className="border-0 shadow-sm">
                          <CardContent className="p-5 space-y-2">
                            {currentLesson.is_fallback && (
                              <Badge variant="secondary">Meest recente gepubliceerde les (nog niet gekoppeld aan een bijeenkomst)</Badge>
                            )}
                            <h3 className="text-lg font-semibold text-slate-900">{currentLesson.lesson.title}</h3>
                            {currentLesson.lesson.theme && <p className="text-slate-600 italic">{currentLesson.lesson.theme}</p>}
                            {currentLesson.lesson.introduction && <p className="text-sm text-slate-600">{currentLesson.lesson.introduction}</p>}
                            {lessonBiblePassages.length > 0 && (
                              <div className="pt-2">
                                <p className="text-xs font-medium uppercase text-slate-400">Bijbelgedeelten</p>
                                {lessonBiblePassages.map((p) => (
                                  <p key={p.id} className="text-sm text-slate-700"><strong>{p.reference}</strong> {p.explanation}</p>
                                ))}
                              </div>
                            )}
                            {lessonQuestions.length > 0 && (
                              <div className="pt-2">
                                <p className="text-xs font-medium uppercase text-slate-400">Gespreksvragen</p>
                                <ol className="list-decimal list-inside space-y-1">
                                  {lessonQuestions.map((q) => (
                                    <li key={q.id} className="text-sm text-slate-700">{q.text}</li>
                                  ))}
                                </ol>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">Nog geen gepubliceerde connectles beschikbaar.</p>
                    )}
                  </TabsContent>

                  <TabsContent value="aanwezigheid" className="mt-4 space-y-3">
                    {attendanceStats.length === 0 ? (
                      <p className="text-sm text-slate-500">Nog geen aanwezigheid geregistreerd. Vink deelnemers af via een bijeenkomst in de Agenda-tab.</p>
                    ) : (
                      <div className="space-y-2">
                        {attendanceStats.map((stat) => (
                          <Card key={stat.member_id} className="border-0 shadow-sm">
                            <CardContent className="p-4 flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-medium text-slate-900">{stat.name}</p>
                                <p className="text-xs text-slate-500">
                                  {stat.times_present} / {stat.meetings_recorded} bijeenkomsten aanwezig
                                  {stat.attendance_rate != null ? ` · ${stat.attendance_rate}%` : ''}
                                  {stat.last_present_date ? ` · Laatst aanwezig: ${stat.last_present_date}` : ' · Nog nooit aanwezig geweest'}
                                </p>
                              </div>
                              {stat.needs_attention && (
                                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 flex items-center gap-1 shrink-0">
                                  <AlertCircle className="w-3.5 h-3.5" /> Al een tijdje niet geweest
                                </span>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            ) : (
              <>
                {canManageGroups && (
                  <Button onClick={() => { resetGroupForm(); setGroupDialogOpen(true); }} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl" data-testid="add-group-btn">
                    <Plus className="w-4 h-4 mr-2" /> Connectgroep toevoegen
                  </Button>
                )}
                {groups.length === 0 ? (
                  <div className="py-12 text-center">
                    <Users2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">Geen connectgroepen</h3>
                    <p className="text-slate-500">Voeg je eerste connectgroep toe om te beginnen</p>
                  </div>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {groups.map((group) => (
                      <Card key={group.id} className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openGroupDetail(group.id)}>
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-semibold text-slate-900">{group.name}</h3>
                              <p className="text-sm text-slate-500">{getLabel(FREQUENCIES, group.frequency)}</p>
                            </div>
                            {canManageGroups && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}><MoreVertical className="w-4 h-4" /></Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditGroup(group); }}><Pencil className="w-4 h-4 mr-2" />Bewerken</DropdownMenuItem>
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.id); }} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Verwijderen</DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                          <p className="mt-2 text-sm text-slate-600">{group.location || 'Geen vaste locatie'}</p>
                          {group.leader_user_id && (
                            <p className="mt-1 text-xs text-slate-500">Leider: {memberNameById[group.leader_user_id] || '-'}</p>
                          )}
                          {!group.is_active && <Badge variant="secondary" className="mt-2">Inactief</Badge>}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="lessen" className="mt-4">
            <LessonLibrary branding={branding} />
          </TabsContent>

          {canManageGroups && (
            <TabsContent value="connectlesmakers" className="space-y-4 mt-4">
              <Button onClick={() => { setWriterDialogOpen(true); setWriterInviteResult(null); }} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                <UserPlus className="w-4 h-4 mr-2" /> Connectlesmaker toevoegen
              </Button>
              {lessonWriters.length === 0 ? (
                <p className="text-sm text-slate-500">Nog geen connectlesmakers toegewezen.</p>
              ) : (
                <div className="space-y-2">
                  {lessonWriters.map((w) => (
                    <Card key={w.id} className="border-0 shadow-sm">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{w.name}</p>
                          <p className="text-xs text-slate-500">{w.email}{w.source_member_id ? ' · Gemeentelid' : ''}</p>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => removeWriter(w.membership_id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* Group create/edit dialog */}
        <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingGroup ? 'Connectgroep bewerken' : 'Connectgroep toevoegen'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleGroupSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Naam *</Label>
                <Input value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>Omschrijving</Label>
                <Textarea value={groupForm.description} onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })} className="rounded-xl resize-none" rows={2} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Connectleider</Label>
                  <Select value={groupForm.leader_user_id || '__none__'} onValueChange={(v) => setGroupForm({ ...groupForm, leader_user_id: v === '__none__' ? '' : v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Nog geen leider</SelectItem>
                      {connectLeaders.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Frequentie</Label>
                  <Select value={groupForm.frequency} onValueChange={(v) => setGroupForm({ ...groupForm, frequency: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Locatie</Label>
                  <Input value={groupForm.location} onChange={(e) => setGroupForm({ ...groupForm, location: e.target.value })} placeholder="bv. bij Jan en Marieke thuis" className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Max. deelnemers</Label>
                  <Input type="number" min="1" value={groupForm.max_participants} onChange={(e) => setGroupForm({ ...groupForm, max_participants: e.target.value })} className="rounded-xl" />
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setGroupDialogOpen(false)} className="rounded-xl">Annuleren</Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">{editingGroup ? 'Opslaan' : 'Toevoegen'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add participant dialog */}
        <Dialog open={participantDialogOpen} onOpenChange={(open) => { setParticipantDialogOpen(open); if (!open) { setMemberSearch(''); setMemberResults([]); } }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>Deelnemer toevoegen</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={memberSearch} onChange={(e) => searchMembers(e.target.value)} placeholder="Zoek op naam of e-mail..." className="pl-9 rounded-xl" />
              </div>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {memberResults.map((m) => (
                  <button key={m.id} type="button" onClick={() => addParticipant(m.id)} className="w-full text-left rounded-lg px-3 py-2 hover:bg-slate-50">
                    <p className="text-sm font-medium text-slate-900">{m.name}</p>
                    <p className="text-xs text-slate-500">{m.email}</p>
                  </button>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Meeting create/edit dialog */}
        <Dialog open={meetingDialogOpen} onOpenChange={setMeetingDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>{editingMeeting ? 'Bijeenkomst bewerken' : 'Bijeenkomst plannen'}</DialogTitle></DialogHeader>
            <form onSubmit={handleMeetingSubmit} className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2 col-span-1">
                  <Label>Datum *</Label>
                  <Input type="date" value={meetingForm.meeting_date} onChange={(e) => setMeetingForm({ ...meetingForm, meeting_date: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Start *</Label>
                  <Input type="time" value={meetingForm.start_time} onChange={(e) => setMeetingForm({ ...meetingForm, start_time: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Eind *</Label>
                  <Input type="time" value={meetingForm.end_time} onChange={(e) => setMeetingForm({ ...meetingForm, end_time: e.target.value })} className="rounded-xl" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Connectles</Label>
                <Select value={meetingForm.lesson_id || '__none__'} onValueChange={(v) => setMeetingForm({ ...meetingForm, lesson_id: v === '__none__' ? '' : v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Geen</SelectItem>
                    {publishedLessons.map((l) => <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Thema (overschrijft les-thema indien ingevuld)</Label>
                <Input value={meetingForm.theme} onChange={(e) => setMeetingForm({ ...meetingForm, theme: e.target.value })} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>Locatie</Label>
                <Input value={meetingForm.location} onChange={(e) => setMeetingForm({ ...meetingForm, location: e.target.value })} className="rounded-xl" />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setMeetingDialogOpen(false)} className="rounded-xl">Annuleren</Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">{editingMeeting ? 'Opslaan' : 'Toevoegen'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Room booking dialog */}
        <Dialog open={!!roomBookingMeetingId} onOpenChange={(open) => { if (!open) { setRoomBookingMeetingId(null); setRoomBookingResourceId(''); setRoomAvailability([]); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Ruimte reserveren</DialogTitle></DialogHeader>
            <div className="space-y-4">
              {loadingRoomAvailability ? (
                <p className="text-sm text-slate-500">Beschikbaarheid controleren...</p>
              ) : roomAvailability.length === 0 ? (
                <p className="text-sm text-slate-500">Geen ruimtes beschikbaar om te reserveren.</p>
              ) : (
                <div className="space-y-1.5">
                  {roomAvailability.map((room) => (
                    <button
                      key={room.resource_id}
                      type="button"
                      disabled={!room.available}
                      onClick={() => setRoomBookingResourceId(room.resource_id)}
                      className={`w-full flex items-center justify-between rounded-xl border px-3 py-2 text-left transition-colors ${
                        roomBookingResourceId === room.resource_id
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-slate-200 hover:bg-slate-50'
                      } ${!room.available ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className="text-sm font-medium text-slate-900">{room.name}</span>
                      {room.available ? (
                        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">Beschikbaar</span>
                      ) : (
                        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-red-100 text-red-700">
                          Bezet{room.conflict_title ? ` · ${room.conflict_title}` : ''}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setRoomBookingMeetingId(null)} className="rounded-xl">Annuleren</Button>
              <Button onClick={handleBookRoom} disabled={!roomBookingResourceId} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">Reserveren</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add lesson-writer dialog */}
        <Dialog open={writerDialogOpen} onOpenChange={(open) => { setWriterDialogOpen(open); if (!open) { setWriterUserId(''); setWriterMemberSearch(''); setWriterMemberResults([]); } }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>Connectlesmaker toevoegen</DialogTitle></DialogHeader>
            {writerInviteResult ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Uitnodiging klaar voor {writerInviteResult.user?.name}:</p>
                <Input readOnly value={writerInviteResult.invite_link} className="rounded-xl font-mono text-xs" onFocus={(e) => e.target.select()} />
                <DialogFooter>
                  <Button onClick={() => setWriterDialogOpen(false)} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">Sluiten</Button>
                </DialogFooter>
              </div>
            ) : (
              <Tabs value={writerSource} onValueChange={setWriterSource}>
                <TabsList>
                  <TabsTrigger value="teamlid">Bestaand teamlid</TabsTrigger>
                  <TabsTrigger value="gemeentelid">Gemeentelid</TabsTrigger>
                </TabsList>
                <TabsContent value="teamlid" className="space-y-3 mt-3">
                  <Select value={writerUserId} onValueChange={setWriterUserId}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Kies een teamlid" /></SelectTrigger>
                    <SelectContent>
                      {availableTeamMembersForWriter.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <DialogFooter>
                    <Button onClick={addWriterFromExisting} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">Toevoegen</Button>
                  </DialogFooter>
                </TabsContent>
                <TabsContent value="gemeentelid" className="space-y-3 mt-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input value={writerMemberSearch} onChange={(e) => searchWriterMembers(e.target.value)} placeholder="Zoek op naam of e-mail..." className="pl-9 rounded-xl" />
                  </div>
                  <div className="max-h-56 overflow-y-auto space-y-1">
                    {writerMemberResults.map((m) => (
                      <button key={m.id} type="button" disabled={!m.email} onClick={() => addWriterFromMember(m.id)} className="w-full text-left rounded-lg px-3 py-2 hover:bg-slate-50 disabled:opacity-40">
                        <p className="text-sm font-medium text-slate-900">{m.name}</p>
                        <p className="text-xs text-slate-500">{m.email || 'Geen e-mailadres'}</p>
                      </button>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>

        {/* Aanwezigheid dialog */}
        <Dialog open={attendanceDialogOpen} onOpenChange={(open) => { setAttendanceDialogOpen(open); if (!open) { setAttendanceMeeting(null); setAttendanceDraft({}); } }}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Aanwezigheid {attendanceMeeting ? `· ${attendanceMeeting.meeting_date}` : ''}</DialogTitle>
            </DialogHeader>
            {participants.length === 0 ? (
              <p className="text-sm text-slate-500">Deze groep heeft nog geen deelnemers.</p>
            ) : (
              <div className="space-y-4">
                <div className="space-y-1">
                  {participants.map((p) => {
                    const present = attendanceDraft[p.member_id];
                    return (
                      <button
                        key={p.member_id}
                        type="button"
                        onClick={() => toggleAttendance(p.member_id)}
                        className="w-full flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 hover:bg-slate-50"
                      >
                        <span className="text-sm font-medium text-slate-900">{congregationMemberNameById[p.member_id] || p.member_id}</span>
                        {present ? (
                          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Aanwezig</span>
                        ) : (
                          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-red-100 text-red-700 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Afwezig</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <DialogFooter className="gap-2 flex-col sm:flex-row">
                  {(attendanceMeeting?.attendance || []).length > 0 && (
                    <Button type="button" variant="outline" onClick={sendAttendanceEmail} className="rounded-xl w-full sm:w-auto">
                      <Mail className="w-4 h-4 mr-2" /> Overzicht mailen
                    </Button>
                  )}
                  <Button onClick={saveAttendance} disabled={savingAttendance} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl w-full sm:w-auto">
                    {savingAttendance ? 'Bezig...' : 'Opslaan'}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ConnectgroepenPage;
